// Paid shout-outs in the TV ticker: the shapes the server sends, and the small rules the
// screens share. The server checks every message itself (src/helpers/shoutouts.ts); what is
// here only keeps the jukebox page's counter and the TV's ticker in step with it.

export type ShoutoutMode = 'off' | 'auto' | 'review';
export type ShoutoutStatus = 'pending_payment' | 'pending_review' | 'approved' | 'rejected' | 'shown' | 'removed';

export const SHOUTOUT_MAX_LENGTH = 80;

/** A shout-out on the TV now. */
export interface LiveShoutout {
  id: number;
  username: string | null;
  profilePicUrl?: string | null;
  message: string;
  /** When it comes off the TV, in this browser's clock. */
  expiresAt: number;
  /**
   * When the server booked its popup, in this browser's clock. Null for one approved before
   * popups existed, which goes straight to the ticker.
   */
  popupStartAt?: number | null;
  popupEndAt?: number | null;
}

/**
 * A popup that started longer ago than this is not shown: a TV switched on, or reconnected,
 * part way through the evening does not replay the popups it missed. Those shout-outs are
 * still in the ticker.
 */
export const POPUP_FRESH_MS = 15 * 1000;

/** One waiting for review, or recently decided, in the control panel. */
export interface ReviewShoutout {
  id: number;
  username: string | null;
  message: string;
  status: ShoutoutStatus;
  /** The buyer's account, null once deleted (nobody to ban). */
  userId?: number | null;
  amountCents: number;
  paidAt: string | null;
  secondsLeft: number | null;
}

export interface ShoutoutOffer {
  amount: number;
  currency: string;
  maxLength: number;
  review: boolean;
  minutes: number;
  popupSeconds?: number;
}

/** An account banned from one player's ticker, in the control panel. */
export interface ShoutoutBan {
  userId: number;
  username: string | null;
  bannedAt: string;
  bannedBy: string | null;
}

/** Characters as a person counts them: an emoji with a skin tone, or a flag, is one. */
export const countCharacters = (text: string): number => {
  const Segmenter = (Intl as any).Segmenter;
  if (typeof Segmenter === 'function') {
    return Array.from(new Segmenter(undefined, { granularity: 'grapheme' }).segment(text) as any).length;
  }
  return Array.from(text).length;
};

/** The server's list, with each one's end time turned into this browser's clock. */
export const fromServer = (rows: any[], now: number = Date.now()): LiveShoutout[] =>
  (Array.isArray(rows) ? rows : [])
    .filter(row => row && typeof row.message === 'string' && Number(row.secondsLeft) > 0)
    .map(row => {
      const starts = Number(row.popupStartsInMs);
      const ends = Number(row.popupEndsInMs);
      const hasPopup =
        row.popupStartsInMs !== null && row.popupStartsInMs !== undefined && Number.isFinite(starts) && Number.isFinite(ends) && ends > starts;
      return {
        id: row.id,
        username: row.username || null,
        profilePicUrl: row.profilePicUrl || null,
        message: row.message,
        expiresAt: now + Number(row.secondsLeft) * 1000,
        popupStartAt: hasPopup ? now + starts : null,
        popupEndAt: hasPopup ? now + ends : null,
      };
    });

export const stillShowing = (shoutouts: LiveShoutout[], now: number = Date.now()) =>
  shoutouts.filter(shoutout => shoutout.expiresAt > now);

/** One popup's turn on the screen, in this browser's clock. */
export interface PopupSlot {
  shoutout: LiveShoutout;
  startAt: number;
  endAt: number;
}

/**
 * When each shout-out pops up. The server books popups one after another; this keeps them
 * from overlapping if two were booked at the same moment, by pushing the later one back.
 * Popups that started more than POPUP_FRESH_MS ago are left out, so they are never replayed.
 */
export const schedulePopups = (shoutouts: LiveShoutout[], now: number = Date.now()): PopupSlot[] => {
  const booked = shoutouts
    .filter(shoutout => typeof shoutout.popupStartAt === 'number' && typeof shoutout.popupEndAt === 'number')
    .sort((a, b) => a.popupStartAt - b.popupStartAt || a.id - b.id);
  let cursor = -Infinity;
  const slots: PopupSlot[] = [];
  booked.forEach(shoutout => {
    const startAt = Math.max(shoutout.popupStartAt, cursor);
    const endAt = startAt + (shoutout.popupEndAt - shoutout.popupStartAt);
    cursor = endAt;
    // A popup longer than the fresh window (SHOUTOUT_POPUP_SECONDS over 15) is never cut short.
    if (startAt > now - Math.max(POPUP_FRESH_MS, endAt - startAt) && endAt > now) {
      slots.push({ shoutout, startAt, endAt });
    }
  });
  return slots;
};

/** The popup on the screen now, if any. */
export const currentPopup = (shoutouts: LiveShoutout[], now: number = Date.now()): PopupSlot | null =>
  schedulePopups(shoutouts, now).find(slot => slot.startAt <= now && now < slot.endAt) || null;

/** What goes in the ticker: everything not waiting for, or in the middle of, its popup. */
export const tickerShoutouts = (shoutouts: LiveShoutout[], now: number = Date.now()): LiveShoutout[] => {
  const popping = new Set(schedulePopups(shoutouts, now).map(slot => slot.shoutout.id));
  return shoutouts.filter(shoutout => shoutout.expiresAt > now && !popping.has(shoutout.id));
};

/** The next moment the popup or the ticker changes, or null when nothing is due. */
export const nextShoutoutChange = (shoutouts: LiveShoutout[], now: number = Date.now()): number | null => {
  const moments: number[] = [];
  schedulePopups(shoutouts, now).forEach(slot => moments.push(slot.startAt, slot.endAt));
  shoutouts.forEach(shoutout => moments.push(shoutout.expiresAt));
  const future = moments.filter(moment => moment > now);
  return future.length > 0 ? Math.min(...future) : null;
};

/**
 * The ticker's items with the shout-outs spread through them, each once per loop. The first
 * goes at the front, so a new one is never far from the start of the strip.
 */
export const mixShoutouts = <T, S>(items: T[], shoutouts: S[]): (T | S)[] => {
  if (shoutouts.length === 0) return items.slice();
  if (items.length === 0) return shoutouts.slice();
  const before: S[][] = items.map(() => []);
  shoutouts.forEach((shoutout, index) => {
    before[Math.floor((index * items.length) / shoutouts.length)].push(shoutout);
  });
  const mixed: (T | S)[] = [];
  items.forEach((item, index) => {
    mixed.push(...before[index], item);
  });
  return mixed;
};
